/**
 * LetterWizard — VM-7d Standalone-Schreiben
 *
 * 3-Schritte-Wizard, um ein Schreiben ohne Umweg über MV/Kontakt zu starten:
 *   1) Bezug wählen  (Mietverhältnis · Liegenschaft · Einheit · Kontakt)
 *   2) Empfänger wählen (Multi-Select; bei Liegenschaft: „Alle Bewohner" = Rundschreiben)
 *   3) → öffnet MessagingDialog mit dem gewählten Kontext
 *
 * Bei mehreren MV (Rundschreiben) muss der User entscheiden:
 *   - „1 Sammelbrief" (ein PDF an alle, Anrede „An die Bewohner…")
 *   - „1 Schreiben pro Empfänger" (klassisch, jeweils in deren Schriftverkehr-Ordner)
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { firstEmail, emailListToText } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  House, Buildings, User, IdentificationCard, ArrowLeft, ArrowRight,
  Envelope, MagnifyingGlass, CheckSquare, Square, PaperPlaneTilt, Users,
} from "@phosphor-icons/react";

// Adressen kommen von /properties als Objekt zurück — überall String-Rendering nötig
const formatAddress = (a) => {
  if (!a) return "";
  if (typeof a === "string") return a;
  const parts = [
    [a.street, a.house_number].filter(Boolean).join(" ").trim(),
    [a.zip, a.city].filter(Boolean).join(" ").trim(),
  ].filter(Boolean);
  return parts.join(", ");
};

// tenant_names kann List oder String sein
const formatNames = (n) => {
  if (!n) return "";
  if (Array.isArray(n)) return n.filter(Boolean).join(", ");
  return String(n);
};

const BEZUG_TYPES = [
  { key: "tenancy", label: "Mietverhältnis", icon: House, desc: "Ein konkretes Mietverhältnis — Anschrift an den Mieter." },
  { key: "property", label: "Liegenschaft", icon: Buildings, desc: "Alle Bewohner einer Liegenschaft — Rundschreiben." },
  { key: "contact", label: "Einzelner Kontakt", icon: User, desc: "Ein Kontakt (Eigentümer, Handwerker, Behörde …)." },
];

export default function LetterWizard({ open, onOpenChange, onOpenComposer }) {
  const [step, setStep] = useState(1);
  const [bezugType, setBezugType] = useState(null);       // 'tenancy' | 'property' | 'contact'
  const [bezugEntity, setBezugEntity] = useState(null);   // ausgewählter Datensatz
  const [selectedRecipients, setSelectedRecipients] = useState([]); // Liste von {tenancy_id, contact_id, key, name, address}
  const [rundschreibenMode, setRundschreibenMode] = useState("per_empfaenger"); // 'per_empfaenger' | 'sammelbrief'

  // Datenquellen
  const [tenancies, setTenancies] = useState([]);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [propertyTenancies, setPropertyTenancies] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search
  const [q, setQ] = useState("");

  const reset = () => {
    setStep(1); setBezugType(null); setBezugEntity(null);
    setSelectedRecipients([]); setRundschreibenMode("per_empfaenger");
    setQ("");
  };

  useEffect(() => { if (open) reset(); }, [open]);

  // Load entities when a bezug-type is picked
  useEffect(() => {
    if (!bezugType) return;
    setLoading(true);
    const call = bezugType === "tenancy" ? api.get("/tenancies", { params: { exclude_status: "archived" } })
      : bezugType === "property" ? api.get("/properties")
      : api.get("/contacts", { params: { limit: 500 } });
    call
      .then(({ data }) => {
        if (bezugType === "tenancy") setTenancies(data || []);
        else if (bezugType === "property") setProperties(data || []);
        else setContacts(data || []);
      })
      .catch(() => toast.error("Konnte Daten nicht laden"))
      .finally(() => setLoading(false));
  }, [bezugType]);

  // If property picked, load all its tenancies AND units (2026-07: auch Einheiten ohne MV)
  useEffect(() => {
    if (bezugType !== "property" || !bezugEntity) { setPropertyTenancies([]); return; }
    setLoading(true);
    Promise.all([
      api.get("/tenancies", { params: { property_id: bezugEntity.id, exclude_status: "archived" } }),
      api.get(`/properties/${bezugEntity.id}`),
    ])
      .then(([tRes, pRes]) => {
        const tenanciesArr = tRes.data || [];
        const unitsArr = pRes.data?.units || [];
        // Einheit-IDs, die schon durch ein aktives MV abgedeckt sind (Haupt- oder Zusatz-Einheit)
        const coveredUnitIds = new Set();
        tenanciesArr.forEach((t) => {
          if (t.unit_id) coveredUnitIds.add(t.unit_id);
          (t.additional_unit_ids || []).forEach((id) => coveredUnitIds.add(id));
        });
        // Recipients aus tenancies (mit Bewohner-Namen)
        const fromTenancies = tenanciesArr.map((t) => ({
          tenancy_id: t.id,
          contact_id: (t.tenant_contact_ids || [])[0],
          unit_id: t.unit_id,
          name: formatNames(t.tenant_names) || t.tenants || "Mieter",
          address: formatAddress(bezugEntity.address),
          unit_label: t.unit_number || "",
          has_tenancy: true,
          key: `tenancy:${t.id}`,
        }));
        // Zusätzliche Einheiten OHNE MV — als eigenständige Empfänger (2026-07)
        const fromUnits = unitsArr
          .filter((u) => !coveredUnitIds.has(u.id))
          .map((u) => ({
            unit_id: u.id,
            property_id: bezugEntity.id,
            name: "An die Bewohner",
            address: formatAddress(bezugEntity.address),
            unit_label: u.unit_number || u.location || "",
            has_tenancy: false,
            key: `unit:${u.id}`,
          }));
        const merged = [...fromTenancies, ...fromUnits];
        setPropertyTenancies(merged);
        setSelectedRecipients(merged);
      })
      .catch(() => setPropertyTenancies([]))
      .finally(() => setLoading(false));
  }, [bezugEntity, bezugType]);

  const filteredList = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (bezugType === "tenancy") {
      return tenancies.filter((t) => {
        if (!query) return true;
        const hay = `${formatNames(t.tenant_names)} ${t.property_name || ""} ${formatAddress(t.property_address)} ${t.unit_number || ""}`.toLowerCase();
        return hay.includes(query);
      });
    }
    if (bezugType === "property") {
      return properties.filter((p) => !query || (p.name + " " + formatAddress(p.address)).toLowerCase().includes(query));
    }
    if (bezugType === "contact") {
      return contacts.filter((c) => !query || `${c.display_name || ""} ${c.company_name || ""} ${emailListToText(c.emails)}`.toLowerCase().includes(query));
    }
    return [];
  }, [bezugType, tenancies, properties, contacts, q]);

  const toggleRecipient = (r) => {
    setSelectedRecipients((prev) => {
      const exists = prev.find((x) => x.key === r.key);
      return exists ? prev.filter((x) => x.key !== r.key) : [...prev, r];
    });
  };
  const selectAll = () => {
    if (bezugType === "property") {
      setSelectedRecipients(propertyTenancies);
    }
  };
  const clearAll = () => setSelectedRecipients([]);

  const goStep2 = (entity) => {
    setBezugEntity(entity);
    setStep(2);
    // Vor-Auswahl für tenancy / contact
    if (bezugType === "tenancy") {
      setSelectedRecipients([{
        tenancy_id: entity.id,
        contact_id: (entity.tenant_contact_ids || [])[0],
        name: formatNames(entity.tenant_names) || entity.tenants || "Mieter",
        address: formatAddress(entity.property_address),
        key: entity.id,
      }]);
    } else if (bezugType === "contact") {
      setSelectedRecipients([{
        contact_id: entity.id,
        name: entity.display_name || entity.company_name || `${entity.first_name || ""} ${entity.last_name || ""}`.trim(),
        address: (entity.addresses && entity.addresses[0]) ? formatAddress(entity.addresses[0]) : "",
        key: entity.id,
      }]);
    }
    // property: auto in useEffect
  };

  const openComposer = () => {
    if (selectedRecipients.length === 0) { toast.error("Bitte mindestens einen Empfänger auswählen"); return; }
    // Erster Empfänger → context der ersten Composer-Öffnung
    const first = selectedRecipients[0];
    const context = {};
    if (first.tenancy_id) context.tenancy_id = first.tenancy_id;
    else if (first.contact_id) context.contact_id = first.contact_id;
    if (bezugType === "property" && bezugEntity) context.property_id = bezugEntity.id;
    onOpenComposer({
      context,
      recipients: selectedRecipients,
      bezugType,
      bezugEntity,
      rundschreiben: bezugType === "property" && selectedRecipients.length > 1,
      rundschreibenMode,
    });
    onOpenChange(false);
  };

  const bezugIcon = (name) => {
    const t = BEZUG_TYPES.find((b) => b.key === name);
    if (!t) return null;
    const I = t.icon;
    return <I size={22} />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[92vh] flex flex-col overflow-hidden" data-testid="letter-wizard">
        <DialogHeader className="border-b border-slate-200 pb-3">
          <DialogTitle className="text-lg flex items-center gap-2">
            <PaperPlaneTilt size={18} /> Neues Schreiben
          </DialogTitle>
          <DialogDescription>
            Schritt {step} von 3 —{" "}
            {step === 1 && "Bezug wählen"}
            {step === 2 && "Empfänger wählen"}
            {step === 3 && "Bestätigung"}
          </DialogDescription>
          {/* Fortschrittsanzeige */}
          <div className="flex items-center gap-1 mt-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? "bg-slate-900" : "bg-slate-200"}`} />
            ))}
          </div>
        </DialogHeader>

        {/* --------- STEP 1: Bezug wählen --------- */}
        {step === 1 && (
          <div className="p-4 space-y-3 overflow-y-auto" data-testid="wizard-step-1">
            <div className="text-sm text-slate-600 mb-2">
              Wofür ist das Schreiben?
            </div>
            <div className="grid gap-3">
              {BEZUG_TYPES.map((b) => {
                const IconEl = b.icon;
                return (
                  <button
                    key={b.key}
                    onClick={() => { setBezugType(b.key); }}
                    className={`text-left p-4 rounded-md border transition-all ${bezugType === b.key ? "border-slate-900 bg-slate-50 ring-2 ring-slate-900" : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"}`}
                    data-testid={`wizard-bezug-${b.key}`}
                  >
                    <div className="flex items-center gap-3">
                      <IconEl size={24} className="shrink-0" />
                      <div>
                        <div className="font-semibold text-slate-800">{b.label}</div>
                        <div className="text-xs text-slate-500">{b.desc}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {bezugType && (
              <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
                <div className="relative">
                  <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder={`${BEZUG_TYPES.find((b) => b.key === bezugType).label} suchen…`}
                    className="pl-9" data-testid="wizard-search" />
                </div>
                <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="wizard-entity-list">
                  {loading && <div className="p-4 text-sm text-slate-500 text-center">Lade …</div>}
                  {!loading && filteredList.length === 0 && <div className="p-4 text-sm text-slate-500 text-center">Keine Treffer</div>}
                  {filteredList.slice(0, 100).map((e) => (
                    <button
                      key={e.id}
                      onClick={() => goStep2(e)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors flex items-center gap-3"
                      data-testid={`wizard-entity-${e.id}`}
                    >
                      {bezugIcon(bezugType)}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 truncate">
                          {bezugType === "tenancy" && `${formatNames(e.tenant_names) || e.tenants || "Mieter"} · ${e.property_name || ""}`}
                          {bezugType === "property" && (e.name || formatAddress(e.address) || "Liegenschaft")}
                          {bezugType === "contact" && (e.display_name || e.company_name || `${e.first_name || ""} ${e.last_name || ""}`.trim())}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {bezugType === "tenancy" && `${formatAddress(e.property_address)}${e.unit_number ? " · Einheit " + e.unit_number : ""}`}
                          {bezugType === "property" && (formatAddress(e.address) || "—")}
                          {bezugType === "contact" && (firstEmail(e.emails) || "—")}
                        </div>
                      </div>
                      <ArrowRight size={14} className="text-slate-400" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* --------- STEP 2: Empfänger wählen --------- */}
        {step === 2 && bezugEntity && (
          <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0" data-testid="wizard-step-2">
            {/* Bezug-Info */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Ausgewählter Bezug</div>
              <div className="font-medium text-slate-800 mt-0.5 flex items-center gap-2">
                {bezugIcon(bezugType)}
                {bezugType === "tenancy" && `${formatNames(bezugEntity.tenant_names) || bezugEntity.tenants || "Mieter"} — ${formatAddress(bezugEntity.property_address)}`}
                {bezugType === "property" && `${bezugEntity.name || ""} — ${formatAddress(bezugEntity.address)}`}
                {bezugType === "contact" && (bezugEntity.display_name || bezugEntity.company_name || `${bezugEntity.first_name || ""} ${bezugEntity.last_name || ""}`.trim())}
              </div>
            </div>

            {/* Bei Property: alle Bewohner-Liste */}
            {bezugType === "property" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-800">
                    <Users size={14} className="inline mr-1" /> Bewohner der Liegenschaft ({propertyTenancies.length})
                  </div>
                  <div className="flex gap-1">
                    <button onClick={selectAll} className="text-xs text-slate-700 hover:text-slate-900 font-medium px-2 py-1 rounded hover:bg-slate-100" data-testid="wizard-select-all">Alle</button>
                    <button onClick={clearAll} className="text-xs text-slate-700 hover:text-slate-900 font-medium px-2 py-1 rounded hover:bg-slate-100" data-testid="wizard-clear-all">Keine</button>
                  </div>
                </div>
                {loading && <div className="text-sm text-slate-500 py-4 text-center">Lade Bewohner …</div>}
                {!loading && propertyTenancies.length === 0 && (
                  <div className="text-sm text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded">
                    Keine aktiven Mietverhältnisse in dieser Liegenschaft.
                  </div>
                )}
                <div className="border border-slate-200 rounded max-h-72 overflow-y-auto divide-y divide-slate-100">
                  {propertyTenancies.map((t) => {
                    const isSelected = !!selectedRecipients.find((r) => r.key === t.key);
                    return (
                      <button
                        key={t.key}
                        onClick={() => toggleRecipient(t)}
                        className={`w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-slate-50 transition-colors ${isSelected ? "bg-emerald-50" : ""}`}
                        data-testid={`wizard-recipient-${t.key}`}
                      >
                        {isSelected ? <CheckSquare size={16} className="text-emerald-700" weight="fill" /> : <Square size={16} className="text-slate-400" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800 truncate">
                            {t.name}
                            {!t.has_tenancy && (
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-normal">ohne Mietverhältnis</span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500">Einheit {t.unit_label || "—"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Rundschreiben-Modus */}
                {selectedRecipients.length > 1 && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2" data-testid="wizard-rundschreiben-mode">
                    <div className="text-xs font-semibold text-amber-900">Rundschreiben-Modus</div>
                    <div className="space-y-1.5">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="radio" name="rmode" checked={rundschreibenMode === "per_empfaenger"}
                          onChange={() => setRundschreibenMode("per_empfaenger")}
                          className="mt-0.5" data-testid="wizard-mode-per-empfaenger" />
                        <div>
                          <div className="text-sm font-medium text-slate-800">Ein Schreiben pro Empfänger</div>
                          <div className="text-[11px] text-slate-600">Jeder Bewohner erhält einen personalisierten Brief. Das Schreiben landet im Schriftverkehr-Ordner jedes Mieters.</div>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="radio" name="rmode" checked={rundschreibenMode === "sammelbrief"}
                          onChange={() => setRundschreibenMode("sammelbrief")}
                          className="mt-0.5" data-testid="wizard-mode-sammelbrief" />
                        <div>
                          <div className="text-sm font-medium text-slate-800">Sammelbrief (An die Bewohner …)</div>
                          <div className="text-[11px] text-slate-600">Ein einziger Brief mit Sammelanschrift &bdquo;An die Bewohner&ldquo;, wird in jedem Schriftverkehr-Ordner abgelegt.</div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bei tenancy / contact: der Empfänger wurde beim Klick im Schritt 1 gewählt */}
            {bezugType !== "property" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                <div className="text-xs font-semibold text-emerald-900 mb-1">Empfänger</div>
                <div className="text-sm text-slate-800">{selectedRecipients[0]?.name}</div>
                <div className="text-[11px] text-slate-500">{selectedRecipients[0]?.address}</div>
              </div>
            )}
          </div>
        )}

        {/* --------- Footer --------- */}
        <div className="border-t border-slate-200 p-3 bg-slate-50 flex items-center justify-between">
          <Button variant="outline" onClick={() => step === 1 ? onOpenChange(false) : setStep(step - 1)} data-testid="wizard-back">
            <ArrowLeft size={14} className="mr-1.5" /> {step === 1 ? "Abbrechen" : "Zurück"}
          </Button>
          <div className="text-xs text-slate-500">
            {step === 2 && selectedRecipients.length > 0 && `${selectedRecipients.length} Empfänger`}
          </div>
          {step === 2 && (
            <Button
              onClick={openComposer}
              disabled={selectedRecipients.length === 0}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="wizard-next"
            >
              Editor öffnen <ArrowRight size={14} className="ml-1.5" />
            </Button>
          )}
          {step === 1 && <div className="w-24" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
